import React, {useState, useMemo} from 'react';
import {connect} from 'react-redux';
import {isMobile} from 'react-device-detect';
import {getBackgroundImageProps} from 'app/utilities/ImageProps';
import classNames from 'classnames';

// Extract font styles (font-size and font-family) from rich text HTML
const extractFontStyles = (htmlString) => {
  if (!htmlString) return {fontSize: null, fontFamily: null};

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    // Look for elements with style attribute containing font properties
    const styledElement = doc.querySelector('[style*="font"]') || doc.querySelector('span, p');

    let fontSize = null;
    let fontFamily = null;

    if (styledElement) {
      const style = styledElement.getAttribute('style') || '';

      // Extract font-size
      const sizeMatch = style.match(/font-size:\s*([^;]+)/i);
      if (sizeMatch) {
        fontSize = sizeMatch[1].trim();
      }

      // Extract font-family
      const familyMatch = style.match(/font-family:\s*([^;]+)/i);
      if (familyMatch) {
        fontFamily = familyMatch[1].trim();
      }
    }

    return {fontSize, fontFamily};
  } catch (e) {
    return {fontSize: null, fontFamily: null};
  }
};

const StickyNoteMain = (props) => {
  const {iContent, context} = props;

  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  // Get responsive values based on device
  const text = isMobile ? iContent?.mobileText?.value : iContent?.desktopText?.value;
  const linkText = isMobile ? iContent?.mobileLinkText?.value : iContent?.desktopLinkText?.value;
  const backgroundImageRef = isMobile ? iContent?.mobileBackgroundImage.value : iContent?.desktopBackgroundImage.value;
  const stripHeight = isMobile ? iContent?.mobileStripHeight?.value || '50' : iContent?.desktopStripHeight?.value || '100';
  // Extract font styles from rich text
  const textFontStyles = useMemo(() => extractFontStyles(text), [text]);

  // Common settings
  const showCloseButton = iContent?.showCloseButton?.value ?? true;
  const linkUrl = iContent?.linkUrl?.value;
  const backgroundColor = iContent?.stickyBackgroundColor?.value || '';
  const textColor = iContent?.textColor?.value || '#ffffff';
  const linkColor = iContent?.linkColor?.value || '#ffffff';

  // Helper function to create gradient text styles
  const getTextStyle = (color) => {
    if (color && color.includes('gradient')) {
      return {
        background: color,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
      };
    }
    return {color};
  };

  const isHeaderHidden = () => {
    const items = props.iContentRepo;
    let currentPath = props.currentPath;
    if (!currentPath) return false;

    const last = currentPath.charAt(currentPath.length - 1);
    if (last !== '/') {
      currentPath += '/';
    }
    const page = Object.values(items).find((item) => item.path === currentPath);
    return page?.content?.hideSiteHeader?.value === true;
  };

  // Handle close
  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsClosing(true);
    setTimeout(() => setIsVisible(false), 300);
  };

  // Handle strip click (when whole strip is clickable)
  const handleClick = () => {
    if (linkUrl && !linkText) {
      window.location.href = linkUrl;
    }
  };

  // Don't render if not visible
  if (!isVisible) return null;

  // If no link text defined - whole strip is clickable
  const isStripClickable = linkUrl && !linkText;

  // Check if backgroundColor contains a gradient
  const isGradient = backgroundColor && backgroundColor.includes('gradient');

  const stripStyle = {
    background: !backgroundColor || isGradient ? 'transparent' : backgroundColor,
    height: `${stripHeight}px`,
    cursor: isStripClickable ? 'pointer' : 'default',
    backgroundImage: backgroundImageRef
      ? `url(${context.getEpiserverUrl(backgroundImageRef)})`
      : isGradient
        ? backgroundColor
        : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  // Placeholder style to reserve space for the fixed element
  const placeholderStyle = {
    height: isClosing ? '0px' : `${stripHeight}px`,
  };

  return (
    <div className={`sticky-note-wrapper `}>
      {/* Placeholder to prevent content from hiding under fixed element */}
      <div className='sticky-note-placeholder' style={placeholderStyle} />

      <div
        className={classNames('sticky-note-block', isHeaderHidden() ? 'header-hidden' : '', {
          visible: isVisible && !isClosing,
          closing: isClosing,
        })}
        style={stripStyle}
        onClick={isStripClickable ? handleClick : undefined}
        role={isStripClickable ? 'link' : undefined}>
        <div className='sticky-note-content'>
          {showCloseButton && (
            <button className='sticky-note-close' onClick={handleClose} aria-label='סגור' style={getTextStyle(textColor)}>
              <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M12 4L4 12M4 4L12 12' stroke='var(--theme-primary, #810EA9)' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
            </button>
          )}
          <div className='sticky-note-text-wrapper'>
            {text && (
              <div
                className='sticky-note-text'
                style={{
                  ...getTextStyle(textColor),
                  fontSize: textFontStyles.fontSize ? textFontStyles.fontSize : isMobile ? '14px' : '18px',
                  fontFamily: textFontStyles.fontFamily ? textFontStyles.fontFamily : 'RAG-Sans-NewCellcom-Regular',
                }}
                dangerouslySetInnerHTML={{__html: text}}
              />
            )}

            {linkText && linkUrl && (
              <div className='sticky-note-link-wrapper'>
                <a
                  href={linkUrl}
                  className='sticky-note-btn'
                  style={{
                    ...getTextStyle(linkColor),
                    fontSize: textFontStyles.fontSize ? textFontStyles.fontSize : isMobile ? '14px' : '18px',
                  }}
                  dangerouslySetInnerHTML={{__html: linkText}}
                />
                <svg
                  className='sticky-note-link-icon'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  style={getTextStyle(linkColor)}>
                  <path d='M10 12L6 8L10 4' stroke='var(--theme-primary, #810EA9)' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  iContentRepo: state.iContentRepo.items,
  currentPath: state.ViewContext.currentPath,
});
export default connect(mapStateToProps)(StickyNoteMain);
